import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import Pagination from "@/pages/category/components/Pagination";
import {
  getCategory,
  getMediaByType,
  getRegions,
  getYears,
  filterMedia,
  sortMedia,
  type SortKey,
} from "@/utils/media";

const PAGE_SIZE = 12;

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "hot", label: "按热门", icon: "ri-fire-line" },
  { key: "new", label: "按最新", icon: "ri-time-line" },
  { key: "rating", label: "按评分", icon: "ri-star-line" },
];

export default function CategoryPage() {
  const { type } = useParams();
  const category = getCategory(type);
  const baseList = useMemo(() => getMediaByType(type), [type]);

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [rating, setRating] = useState("全部");
  const [sort, setSort] = useState<SortKey>("hot");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const regions = useMemo(() => getRegions(baseList), [baseList]);
  const years = useMemo(() => getYears(baseList), [baseList]);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setRating("全部");
    setSort("hot");
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [region, year, rating, sort]);

  const result = useMemo(() => {
    const filtered = filterMedia(baseList, { region, year, rating });
    return sortMedia(filtered, sort);
  }, [baseList, region, year, rating, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const paged = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setRating("全部");
  };

  if (!category) {
    return (
      <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-foreground-400">
          <i className="ri-emotion-sad-line" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-900">分类不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的影视分类不存在或已下线。</p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const filterProps = {
    regions,
    years,
    region,
    year,
    rating,
    onRegion: setRegion,
    onYear: setYear,
    onRating: setRating,
    onReset: resetFilters,
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="font-medium text-foreground-800">{category.name}</span>
      </nav>

      <div className="mt-5 flex flex-col gap-1">
        <h1 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className={`${category.icon} text-2xl`} />
          </span>
          {category.name}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{category.desc}</p>
        <p className="mt-1 text-sm text-foreground-500">
          当前共收录 <strong className="text-accent-600">{baseList.length}</strong> 部作品，筛选后剩{" "}
          <strong className="text-accent-600">{result.length}</strong> 部
        </p>
      </div>

      <div className="mt-8 flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-20">
            <FilterPanel {...filterProps} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-background-50 px-4 py-3 ring-1 ring-background-200">
            <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    sort === option.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  <i className={option.icon} />
                  {option.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setMobileFilterOpen(true)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:hidden"
            >
              <i className="ri-filter-3-line" />
              筛选
            </button>
          </div>

          {paged.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {paged.map((item) => (
                  <MediaCard key={item.id} media={item} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-5 flex flex-col items-center rounded-lg bg-background-50 px-6 py-16 text-center ring-1 ring-background-200">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-3xl text-accent-700">
                <i className="ri-search-eye-line" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">没有找到符合条件的影视</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-500">
                尝试调整地区、年份或评分区间，或者点击下方按钮重置全部筛选条件。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 rounded-md bg-accent-500 px-6 py-2.5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end bg-primary-950/50 lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            className="absolute inset-0"
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className="relative max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-900">筛选作品</span>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-xl text-foreground-600"
                aria-label="关闭"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <FilterPanel {...filterProps} />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-4 w-full rounded-md bg-primary-500 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              查看 {result.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}